import React, { useState, useEffect } from 'react'
import { getConfiguracoes, updateConfiguracao } from '../../lib/supabase'
import { MOTIVOS_BLOQUEIO, DURACAO_MINIMA_BLOQUEIO } from '../../lib/bloqueios'
import { useTenant } from '../../contexts/TenantContext'
import { useToast } from '../../contexts/ToastContext'
import Loading from '../../components/Loading'

const DIAS = [
  { v: 2, l: 'Segunda' }, { v: 3, l: 'Terça'  }, { v: 4, l: 'Quarta' },
  { v: 5, l: 'Quinta'  }, { v: 6, l: 'Sexta'  }, { v: 7, l: 'Sábado' },
  { v: 1, l: 'Domingo' },
]

const inputStyle = {
  padding: '9px 12px', border: '1px solid var(--cor-borda)',
  borderRadius: 'var(--raio)', fontSize: '14px',
  fontFamily: 'var(--font-ui)', background: 'var(--cor-branco)', color: 'var(--cor-texto)',
  appearance: 'none', WebkitAppearance: 'none',
  textAlign: 'left', minHeight: '38px', boxSizing: 'border-box',
}

function Secao({ titulo, children }) {
  return (
    <div style={{
      background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)',
      borderRadius: 'var(--raio-lg)', padding: '24px', marginBottom: '20px',
      boxShadow: 'var(--sombra)',
    }}>
      <p style={{ fontWeight: '600', color: 'var(--cor-principal)', marginBottom: '18px' }}>{titulo}</p>
      {children}
    </div>
  )
}

function Campo({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>{label}</label>
      {children}
    </div>
  )
}

// Textarea com botões de variáveis (igual ao SMS, mas sem limite de 160) — para email
const TAG_CHARS = { '{nome}': 15, '{data}': 10, '{hora}': 5, '{idade}': 2 }

function contarCharsReais(texto) {
  return texto.replace(/\{nome\}|\{data\}|\{hora\}|\{idade\}/g, tag => ' '.repeat(TAG_CHARS[tag] || 0)).length
}

function SmsTextarea({ valor, onChange, placeholder, tags = ['{nome}', '{data}', '{hora}'] }) {
  const charsReais = contarCharsReais(valor)
  const excede = charsReais > 160
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <div style={{ marginBottom: '4px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {tags.map(tag => (
          <button
            key={tag}
            type="button"
            onClick={() => onChange(valor + tag)}
            style={{ padding: '3px 10px', fontSize: '12px', borderRadius: 'var(--raio)', border: '1px solid var(--cor-principal)', background: 'transparent', color: 'var(--cor-principal)', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}
          >
            {tag}
          </button>
        ))}
      </div>
      <div style={{ position: 'relative' }}>
        <textarea
          value={valor}
          onChange={e => onChange(e.target.value)}
          rows={4}
          style={{ ...inputStyle, width: '100%', resize: 'vertical', paddingBottom: '24px', borderColor: excede ? '#DC2626' : undefined }}
          placeholder={placeholder}
        />
        <span style={{ position: 'absolute', right: '10px', bottom: '8px', fontSize: '12px', color: excede ? '#DC2626' : charsReais >= 140 ? '#F59E0B' : 'var(--cor-texto-suave)' }}>
          {charsReais}/160
        </span>
      </div>
      {excede && (
        <p style={{ fontSize: '12px', color: '#DC2626', marginTop: '2px' }}>
          A mensagem ultrapassa os 160 caracteres e será enviada em 2 SMS.
        </p>
      )}
    </div>
  )
}

export default function Configuracoes() {
  const { tenant } = useTenant()
  const toast = useToast()
  const [cfg, setCfg] = useState(null)
  const [guardando, setGuardando] = useState(false)
  const [novoMotivo, setNovoMotivo] = useState('')

  useEffect(() => {
    if (!tenant) return
    getConfiguracoes(tenant.id)
      .then(data => setCfg({
        ...data,
        dias_trabalho: data.dias_trabalho || '2,3,4,5,6',
        motivos_bloqueio: data.motivos_bloqueio || JSON.stringify(MOTIVOS_BLOQUEIO),
        bloqueio_duracao_minima: data.bloqueio_duracao_minima || String(DURACAO_MINIMA_BLOQUEIO),
      }))
      .catch(e => toast.erro(e.message))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant])

  function set(chave, valor) {
    setCfg(prev => ({ ...prev, [chave]: valor }))
  }

  function motivosBloqueio() {
    try {
      const lista = JSON.parse(cfg.motivos_bloqueio || '[]')
      return Array.isArray(lista) ? lista : []
    } catch {
      return []
    }
  }

  function adicionarMotivo() {
    const texto = novoMotivo.trim()
    if (!texto) return
    const atuais = motivosBloqueio()
    if (atuais.some(m => m.toLowerCase() === texto.toLowerCase())) { setNovoMotivo(''); return }
    set('motivos_bloqueio', JSON.stringify([...atuais, texto]))
    setNovoMotivo('')
  }

  function removerMotivo(indice) {
    set('motivos_bloqueio', JSON.stringify(motivosBloqueio().filter((_, i) => i !== indice)))
  }

  function toggleDia(v) {
    const atuais = cfg.dias_trabalho.split(',').map(Number).filter(Boolean)
    const novos = atuais.includes(v)
      ? atuais.filter(d => d !== v)
      : [...atuais, v].sort((a, b) => a - b)
    set('dias_trabalho', novos.join(','))
  }

  async function guardar(e) {
    e.preventDefault()
    setGuardando(true)
    try {
      const campos = [
        'hora_abertura', 'hora_fecho', 'slot_minutos', 'dias_trabalho',
        'antecedencia_horas',
        'almoco_ativo', 'hora_almoco_inicio', 'hora_almoco_fim',
        'nome_negocio', 'nome_fiscal', 'nif_empresa', 'telefone', 'email_contacto', 'morada',
        'politica_finalidade', 'politica_conservacao',
        'politica_texto_extra', 'politica_subcontratantes',
        'termos_texto', 'termos_cancelamento', 'termos_pagamentos', 'termos_idade',
        'sms_sender_id', 'sms_confirmacao_ativo', 'sms_mensagem',
        'sms_lembrete_ativo', 'sms_lembrete_hora', 'sms_lembrete_dias_antes', 'sms_mensagem_lembrete',
        'aniversario_sms_ativo', 'aniversario_sms_hora', 'aniversario_sms_mensagem',
        'motivos_bloqueio', 'bloqueio_duracao_minima',
      ]
      await Promise.all(campos.map(c => updateConfiguracao(tenant.id, c, cfg[c] || '')))
      toast.sucesso('Configurações guardadas com sucesso.')
    } catch (e) {
      toast.erro(e.message)
    } finally {
      setGuardando(false)
    }
  }

  if (!cfg) return <div style={{ padding: '64px 24px' }}><Loading /></div>

  const diasAtivos = cfg.dias_trabalho.split(',').map(Number).filter(Boolean)

  return (
    <div style={{ maxWidth: '620px', margin: '0 auto', padding: '32px 24px 64px' }}>
      <h2 style={{ marginBottom: '6px', fontSize: '22px' }}>Configurações</h2>
      <p style={{ color: 'var(--cor-texto-suave)', marginBottom: '32px', fontSize: '14px' }}>
        Horários, dias de funcionamento e informações do negócio.
      </p>

      <form onSubmit={guardar}>
        <Secao titulo="Horário de Funcionamento">
          <div className="grid-config-4col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px' }}>
            <Campo label="Abertura">
              <input type="time" value={cfg.hora_abertura || ''} onChange={e => set('hora_abertura', e.target.value)} style={{ ...inputStyle, width: '100%' }} />
            </Campo>
            <Campo label="Fecho">
              <input type="time" value={cfg.hora_fecho || ''} onChange={e => set('hora_fecho', e.target.value)} style={{ ...inputStyle, width: '100%' }} />
            </Campo>
            <Campo label="Slot (min)">
              <input type="number" min="5" max="120" step="5" value={cfg.slot_minutos || ''} onChange={e => set('slot_minutos', e.target.value)} style={{ ...inputStyle, width: '100%' }} />
            </Campo>
            <Campo label="Antecedência (h)">
              <input type="number" min="0" max="48" step="1" value={cfg.antecedencia_horas || ''} onChange={e => set('antecedencia_horas', e.target.value)} style={{ ...inputStyle, width: '100%' }} placeholder="0" />
            </Campo>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '18px', cursor: 'pointer', fontSize: '14px' }}>
            <input
              type="checkbox"
              checked={cfg.almoco_ativo === 'true'}
              onChange={e => set('almoco_ativo', e.target.checked ? 'true' : 'false')}
              style={{ width: '16px', height: '16px', accentColor: 'var(--cor-principal)' }}
            />
            <span>Encerramento para almoço</span>
          </label>

          {cfg.almoco_ativo === 'true' && (
            <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginTop: '12px' }}>
              <Campo label="Início do almoço">
                <input type="time" value={cfg.hora_almoco_inicio || ''} onChange={e => set('hora_almoco_inicio', e.target.value)} style={{ ...inputStyle, width: '100%' }} />
              </Campo>
              <Campo label="Fim do almoço">
                <input type="time" value={cfg.hora_almoco_fim || ''} onChange={e => set('hora_almoco_fim', e.target.value)} style={{ ...inputStyle, width: '100%' }} />
              </Campo>
            </div>
          )}
        </Secao>

        <Secao titulo="Dias de Atendimento">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {DIAS.map(({ v, l }) => {
              const ativo = diasAtivos.includes(v)
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => toggleDia(v)}
                  style={{
                    padding: '8px 16px', borderRadius: 'var(--raio)',
                    border: `2px solid ${ativo ? 'var(--cor-principal)' : 'var(--cor-borda)'}`,
                    background: ativo ? 'var(--cor-principal)' : 'var(--cor-branco)',
                    color: ativo ? 'white' : 'var(--cor-texto)',
                    fontSize: '14px', cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  {l}
                </button>
              )
            })}
          </div>
        </Secao>

        <Secao titulo="Motivos de Bloqueio">
          <p style={{ fontSize: '13px', color: 'var(--cor-texto-suave)', marginBottom: '16px', lineHeight: '1.5' }}>
            Lista de motivos disponível ao criar um bloqueio, na Agenda do Dia e na página Bloqueios. Tem de ficar pelo menos um motivo na lista.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
            {motivosBloqueio().map((m, i) => (
              <span key={m} style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                padding: '6px 6px 6px 14px', borderRadius: '20px',
                border: '1px solid var(--cor-borda)', background: 'var(--cor-creme)',
                fontSize: '13px',
              }}>
                {m}
                <button
                  type="button"
                  onClick={() => removerMotivo(i)}
                  disabled={motivosBloqueio().length <= 1}
                  title={motivosBloqueio().length <= 1 ? 'Tem de existir pelo menos um motivo' : `Remover "${m}"`}
                  style={{
                    border: 'none', background: 'none', padding: '0 6px', lineHeight: 1, fontSize: '16px',
                    color: motivosBloqueio().length <= 1 ? 'var(--cor-borda)' : '#DC2626',
                    cursor: motivosBloqueio().length <= 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              type="text"
              value={novoMotivo}
              onChange={e => setNovoMotivo(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); adicionarMotivo() } }}
              style={{ ...inputStyle, flex: 1 }}
              placeholder="Novo motivo…"
              maxLength={40}
            />
            <button type="button" onClick={adicionarMotivo} className="btn-secundario" style={{ padding: '9px 16px' }}>
              Adicionar
            </button>
          </div>
          <div style={{ marginTop: '18px', borderTop: '1px solid var(--cor-borda)', paddingTop: '18px' }}>
            <Campo label="Duração mínima de um bloqueio (minutos)">
              <input
                type="number" min="1" step="1"
                value={cfg.bloqueio_duracao_minima || ''}
                onChange={e => set('bloqueio_duracao_minima', e.target.value)}
                style={{ ...inputStyle, width: '140px' }}
                placeholder="5"
              />
            </Campo>
          </div>
        </Secao>

        <Secao titulo="Informações do Negócio">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Campo label="Nome do negócio">
              <input type="text" value={cfg.nome_negocio || ''} onChange={e => set('nome_negocio', e.target.value)} style={{ ...inputStyle, width: '100%' }} />
            </Campo>
            <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <Campo label="Nome fiscal (empresa)">
                <input type="text" value={cfg.nome_fiscal || ''} onChange={e => set('nome_fiscal', e.target.value)} style={{ ...inputStyle, width: '100%' }} placeholder="Denominação legal da empresa" />
              </Campo>
              <Campo label="NIF">
                <input type="text" inputMode="numeric" value={cfg.nif_empresa || ''} onChange={e => set('nif_empresa', e.target.value)} style={{ ...inputStyle, width: '100%' }} placeholder="000000000" />
              </Campo>
            </div>
            <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <Campo label="Telefone">
                <input type="text" value={cfg.telefone || ''} onChange={e => set('telefone', e.target.value)} style={{ ...inputStyle, width: '100%' }} placeholder="+351 900 000 000" />
              </Campo>
              <Campo label="Email de contacto">
                <input type="email" value={cfg.email_contacto || ''} onChange={e => set('email_contacto', e.target.value)} style={{ ...inputStyle, width: '100%' }} placeholder="geral@exemplo.com" />
              </Campo>
            </div>
            <Campo label="Morada">
              <input type="text" value={cfg.morada || ''} onChange={e => set('morada', e.target.value)} style={{ ...inputStyle, width: '100%' }} placeholder="Rua, Nº, Cidade" />
            </Campo>
          </div>
        </Secao>

        <Secao titulo="Notificações SMS">
          <p style={{ fontSize: '13px', color: 'var(--cor-texto-suave)', marginBottom: '16px', lineHeight: '1.5' }}>
            Configure o remetente e as mensagens enviadas automaticamente por SMS. O lembrete só é enviado se a confirmação estiver activa.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Campo label="Remetente (máx. 11 caracteres)">
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={cfg.sms_sender_id || ''}
                  onChange={e => set('sms_sender_id', e.target.value.slice(0, 11))}
                  maxLength={11}
                  style={{ ...inputStyle, width: '100%', paddingRight: '52px' }}
                  placeholder="Remetente do SMS"
                />
                <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: (cfg.sms_sender_id || '').length === 11 ? '#DC2626' : 'var(--cor-texto-suave)' }}>
                  {(cfg.sms_sender_id || '').length}/11
                </span>
              </div>
            </Campo>

            {/* Confirmação */}
            <div style={{ borderTop: '1px solid var(--cor-borda)', paddingTop: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '14px' }}>
                <input
                  type="checkbox"
                  checked={cfg.sms_confirmacao_ativo === 'true'}
                  onChange={e => set('sms_confirmacao_ativo', e.target.checked ? 'true' : 'false')}
                  style={{ width: '16px', height: '16px', accentColor: 'var(--cor-principal)' }}
                />
                <span style={{ fontSize: '14px', fontWeight: '500' }}>Enviar SMS na confirmação da marcação</span>
              </label>
              {cfg.sms_confirmacao_ativo === 'true' && (
                <SmsTextarea
                  chave="sms_mensagem"
                  valor={cfg.sms_mensagem || ''}
                  onChange={v => set('sms_mensagem', v)}
                  placeholder="{nome}, a sua marcacao esta confirmada para {data} as {hora}. Ate breve!"
                />
              )}
            </div>

            {/* Lembrete */}
            <div style={{ borderTop: '1px solid var(--cor-borda)', paddingTop: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '14px' }}>
                <input
                  type="checkbox"
                  checked={cfg.sms_lembrete_ativo === 'true'}
                  onChange={e => set('sms_lembrete_ativo', e.target.checked ? 'true' : 'false')}
                  style={{ width: '16px', height: '16px', accentColor: 'var(--cor-principal)' }}
                />
                <span style={{ fontSize: '14px', fontWeight: '500' }}>Enviar SMS de lembrete no dia anterior</span>
              </label>
              {cfg.sms_lembrete_ativo === 'true' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                    <Campo label="Dias antes da marcação">
                      <input
                        type="number"
                        min="1"
                        max="30"
                        step="1"
                        value={cfg.sms_lembrete_dias_antes || '1'}
                        onChange={e => set('sms_lembrete_dias_antes', e.target.value)}
                        style={{ ...inputStyle, width: '140px' }}
                      />
                    </Campo>
                    <Campo label="Hora de envio do lembrete (hora de Lisboa)">
                      <input
                        type="number"
                        min="0"
                        max="23"
                        value={cfg.sms_lembrete_hora || '18'}
                        onChange={e => set('sms_lembrete_hora', e.target.value)}
                        style={{ ...inputStyle, width: '80px' }}
                        placeholder="18"
                      />
                      <span style={{ fontSize: '12px', color: 'var(--cor-texto-suave)', marginTop: '4px' }}></span>
                    </Campo>
                  </div>
                  <SmsTextarea
                    chave="sms_mensagem_lembrete"
                    valor={cfg.sms_mensagem_lembrete || ''}
                    onChange={v => set('sms_mensagem_lembrete', v)}
                    placeholder="{nome}, lembrete: tem marcacao amanha, {data} as {hora}. Ate breve!"
                  />
                </div>
              )}
            </div>
          </div>
        </Secao>

        <Secao titulo="Notificações de Aniversário">
          <p style={{ fontSize: '13px', color: 'var(--cor-texto-suave)', marginBottom: '16px', lineHeight: '1.5' }}>
            Mensagem enviada automaticamente no dia de aniversário do cliente (com base na data de
            nascimento registada na ficha). Variáveis disponíveis: {'{nome}'} e {'{idade}'}.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* SMS de aniversário */}
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '14px' }}>
                <input
                  type="checkbox"
                  checked={cfg.aniversario_sms_ativo === 'true'}
                  onChange={e => set('aniversario_sms_ativo', e.target.checked ? 'true' : 'false')}
                  style={{ width: '16px', height: '16px', accentColor: 'var(--cor-principal)' }}
                />
                <span style={{ fontSize: '14px', fontWeight: '500' }}>Enviar SMS de aniversário</span>
              </label>
              {cfg.aniversario_sms_ativo === 'true' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <Campo label="Hora de envio (hora de Lisboa)">
                    <input
                      type="number"
                      min="0"
                      max="23"
                      value={cfg.aniversario_sms_hora || '9'}
                      onChange={e => set('aniversario_sms_hora', e.target.value)}
                      style={{ ...inputStyle, width: '80px' }}
                      placeholder="9"
                    />
                    <span style={{ fontSize: '12px', color: 'var(--cor-texto-suave)', marginTop: '4px' }}></span>
                  </Campo>
                  <SmsTextarea
                    valor={cfg.aniversario_sms_mensagem || ''}
                    onChange={v => set('aniversario_sms_mensagem', v)}
                    tags={['{nome}', '{idade}']}
                    placeholder="Parabens {nome}! A M.Mesquita deseja-lhe um feliz aniversario."
                  />
                </div>
              )}
            </div>

          </div>
        </Secao>

        <Secao titulo="Política de Privacidade (RGPD)">
          <p style={{ fontSize: '13px', color: 'var(--cor-texto-suave)', marginBottom: '16px', lineHeight: '1.5' }}>
            O responsável pelo tratamento, NIF, morada e email são obtidos das <strong>Informações do Negócio</strong> acima. Aqui configura apenas o conteúdo específico da política.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Campo label="Finalidade do tratamento de dados">
              <textarea value={cfg.politica_finalidade || ''} onChange={e => set('politica_finalidade', e.target.value)} style={{ ...inputStyle, width: '100%', minHeight: '80px', resize: 'vertical' }} />
            </Campo>
            <Campo label="Prazo de conservação dos dados">
              <input type="text" value={cfg.politica_conservacao || ''} onChange={e => set('politica_conservacao', e.target.value)} style={{ ...inputStyle, width: '100%' }} placeholder="3 anos após a última marcação…" />
            </Campo>
            <Campo label="Subcontratantes / fornecedores que tratam dados">
              <textarea value={cfg.politica_subcontratantes || ''} onChange={e => set('politica_subcontratantes', e.target.value)} style={{ ...inputStyle, width: '100%', minHeight: '70px', resize: 'vertical' }} placeholder="Supabase · Resend · Bulkgate · Cloudflare · Stripe…" />
            </Campo>
            <Campo label="Informações adicionais (opcional)">
              <textarea value={cfg.politica_texto_extra || ''} onChange={e => set('politica_texto_extra', e.target.value)} style={{ ...inputStyle, width: '100%', minHeight: '80px', resize: 'vertical' }} placeholder="Texto extra que aparece no final da política de privacidade…" />
            </Campo>
          </div>
        </Secao>

        <Secao titulo="Termos e Condições">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Campo label="Idade mínima / menores">
              <textarea value={cfg.termos_idade || ''} onChange={e => set('termos_idade', e.target.value)} style={{ ...inputStyle, width: '100%', minHeight: '70px', resize: 'vertical' }} placeholder="Serviço para maiores de 18 anos; menores com autorização do responsável…" />
            </Campo>
            <Campo label="Política de cancelamento / faltas">
              <textarea value={cfg.termos_cancelamento || ''} onChange={e => set('termos_cancelamento', e.target.value)} style={{ ...inputStyle, width: '100%', minHeight: '70px', resize: 'vertical' }} placeholder="Cancelamentos com 24h de antecedência…" />
            </Campo>
            <Campo label="Condições de pagamento / reembolso">
              <textarea value={cfg.termos_pagamentos || ''} onChange={e => set('termos_pagamentos', e.target.value)} style={{ ...inputStyle, width: '100%', minHeight: '70px', resize: 'vertical' }} placeholder="Sinal via Stripe, restante no espaço…" />
            </Campo>
            <Campo label="Texto completo (opcional — substitui os termos por omissão)">
              <textarea value={cfg.termos_texto || ''} onChange={e => set('termos_texto', e.target.value)} style={{ ...inputStyle, width: '100%', minHeight: '120px', resize: 'vertical' }} placeholder="Se preenchido, mostra este texto em vez das secções por omissão…" />
            </Campo>
          </div>
        </Secao>

        <button
          type="submit"
          className="btn-primario"
          disabled={guardando}
          style={{ opacity: guardando ? 0.6 : 1, width: '100%', padding: '14px' }}
        >
          {guardando ? 'A guardar…' : 'Guardar Configurações'}
        </button>
      </form>
    </div>
  )
}
